<template>
  <div class="p-field">
    <div class="p-field-horizontal">
      <div
        class="p-radiobutton-container"
        :class="{
          'p-invalid': isInvalid,
          'p-disabled': disabled
        }"
      >
        <label v-if="label" class="label-text">
          {{ label }}
          <span v-if="required" class="text-red-500">*</span>
        </label>
        <div class="p-radiobutton-group flex flex-wrap" :class="direction == 'row' ? 'flex-row':'flex-col'">
          <div v-for="(option, index) in options" :key="index" class="p-radiobutton-item m-2">
            <RadioButton
              ref="inputRef"
              :id="id ? `${id}-${index}` : undefined"
              v-model="localValue"
              :value="option.value"
              :disabled="disabled || option.disabled"
              :class="[
                'mop-radiobutton',
                {
                  'p-radiobutton-sm': size === 'small',
                  'p-radiobutton-lg': size === 'large',
                  'p-invalid': isInvalid,
                  'p-disabled': disabled || option.disabled
                }
              ]"
              :readonly="readonly"
              :required="required"
              :name="name"
              :aria-label="props['aria-label']"
              :aria-labelledby="props['aria-labelledby']"
              :aria-invalid="isInvalid"
              :aria-required="required"
              :aria-describedby="ariaDescribedBy"
              v-bind="$attrs"
              @input="onInput"
              @change="onChange"
              @focus="onFocus"
              @blur="onBlur"
            />
            <label
              :for="id ? `${id}-${index}` : undefined"
              class="p-radiobutton-label ml-2"
              :class="{ 'p-disabled': disabled || option.disabled }"
            >
              {{ option.label }}
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Error Messages -->
    <div v-if="showErrorMessages" class="p-error-messages">
      <small v-if="helperText" :id="helperTextId" class="p-error block">{{ helperText }}</small>
      <small
        v-for="(error, index) in localizedErrors"
        :key="index"
        :id="`${id}-error-${index}`"
        class="p-error block"
      >
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import RadioButton from 'primevue/radiobutton'

export default defineComponent({
  name: 'MOPRadioButton',
  components: {
    RadioButton
  },
  inheritAttrs: false
})
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useFormContext } from '../../../../composables/useFormContext'
import { useLocalization } from '../../../../composables/useLocalization'
import type { MOPRadioButtonProps } from './types'

const props = withDefaults(defineProps<MOPRadioButtonProps>(), {
  modelValue: undefined,
  options: () => [],
  size: undefined,
  invalid: false,
  disabled: false,
  readonly: false,
  required: false,
  direction:'',
  helperText: '',
  label: '',
  'aria-label': '',
  'aria-labelledby': '',
  id: () => `mop-radiobutton-${Math.random().toString(36).substr(2, 9)}`,
  validationRules: () => [],
  validateOnBlur: true,
  validateOnInput: false,
  validateOnChange: true,
  showErrors: true,
  name: undefined,
  formContext: true,
  wrapperClass: undefined
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'input', event: Event): void
  (e: 'change', event: Event): void
  (e: 'focus', event: Event): void
  (e: 'blur', event: Event): void
  (e: 'validation-error', errors: string[]): void
  (e: 'validation-success'): void
}>()

// Form Context
const { formState } = useFormContext()
const { t } = useLocalization()

// Local State
const inputRef = ref<HTMLInputElement | null>(null)
const localValidationErrors = ref<string[]>([])
const isDirty = ref(false)
const isValidating = ref(false)

const localValue = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit('update:modelValue', newValue)
    if (formState && props.name) {
      formState.setFieldValue(props.name, newValue)
    }
  }
})

const currentErrors = computed(() => {
  if (formState && props.name) {
    return formState.errors.value[props.name] || []
  }
  return localValidationErrors.value
})

const localizedErrors = computed(() => {
  return currentErrors.value.map(error => {
    if (error.startsWith('validation.')) {
      return t(error)
    }
    if (error.toLowerCase().includes('required')) {
      return t('validation.required')
    }
    return error
  })
})

const isInvalid = computed(() => {
  return props.invalid || (currentErrors.value.length > 0 && isDirty.value)
})

const helperTextId = computed(() => `${props.id}-helper`)

const ariaDescribedBy = computed(() => {
  const ids = []
  if (props.helperText) ids.push(helperTextId.value)
  currentErrors.value.forEach((_, index) => ids.push(`${props.id}-error-${index}`))
  return ids.join(' ')
})

const showErrorMessages = computed(() => {
  return props.showErrors && (!!props.helperText || (currentErrors.value.length > 0 && isDirty.value))
})

const validate = async (): Promise<boolean> => {
  if (formState && props.name) {
    return formState.validateField(props.name)
  }

  isValidating.value = true
  try {
    const errors: string[] = []
    const value = localValue.value

    for (const rule of props.validationRules) {
      let isValid = true
      const message = rule.message

      if (rule.validator) {
        isValid = await rule.validator(value)
      }

      if (!isValid && message) {
        errors.push(message)
      }
    }

    localValidationErrors.value = errors
    if (errors.length > 0) {
      emit('validation-error', errors)
      return false
    }

    emit('validation-success')
    return true
  } finally {
    isValidating.value = false
  }
}

const onInput = async (event: Event) => {
  emit('input', event)
  isDirty.value = true
  if (props.validateOnInput) {
    await validate()
  }
}

const onFocus = (event: Event) => {
  emit('focus', event)
}

const onBlur = async (event: Event) => {
  emit('blur', event)
  isDirty.value = true
  if (formState && props.name) {
    formState.setFieldTouched(props.name, true)
  }
  if (props.validateOnBlur) {
    await validate()
  }
}

const onChange = async (event: Event) => {
  emit('change', event)
  if (props.validateOnChange) {
    await validate()
  }
}

// Initialize form context
onMounted(() => {
  if (formState && props.name && props.formContext && props.validationRules.length > 0) {
    formState.addValidation(props.name, props.validationRules)
  }
})

// Watch for external validation triggers
watch(() => props.invalid, (newValue) => {
  if (newValue) {
    isDirty.value = true
  }
})

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
  validate,
  validationErrors: currentErrors,
  isDirty,
  isValidating,
  isValid: computed(() => currentErrors.value.length === 0)
})
</script>
